import { useCallback, useEffect, useMemo, useState } from "react";
import type { AiCapabilities, AiObjectCandidate, AiPhotoOutput, PhotoAiRequest, PhotoAiResponse } from "./types";

type RequestWithoutID = PhotoAiRequest extends infer Request ? (Request extends { id: string } ? Omit<Request, "id"> : never) : never;

interface PendingRequest {
  resolve: (value: PhotoAiResponse) => void;
  reject: (error: Error) => void;
}

export interface PhotoAiState {
  capabilities?: AiCapabilities;
  isLoading: boolean;
  isProcessing: boolean;
  progress: number;
  status: string;
  error?: string;
  lastResult?: AiPhotoOutput;
  lastObjects?: AiObjectCandidate[];
}

const initialPhotoAiState: PhotoAiState = {
  isLoading: false,
  isProcessing: false,
  progress: 0,
  status: "Photo tools idle"
};

let sharedWorker: Worker | null = null;
let sharedRequestCounter = 0;
let sharedState: PhotoAiState = initialPhotoAiState;
const sharedPending = new Map<string, PendingRequest>();
const subscribers = new Set<(state: PhotoAiState) => void>();
const TECHNICAL_PHOTO_AI_TEXT =
  /\b(onnx|webgpu|wasm|webassembly|ben2|rmbg|modnet|birefnet|fp16|fp32|q8|dtype|model|segmentation|matting|tensor|alpha mask|shader|storage buffer|storage-buffer|maxstoragebuffers|adapter|runtime|worker|offscreen|navigator\.gpu)\b/i;

function emitState(updater: (current: PhotoAiState) => PhotoAiState) {
  sharedState = updater(sharedState);
  subscribers.forEach((subscriber) => subscriber(sharedState));
}

function safePhotoAiStatus(message: string, fallback = "Preparing background tools") {
  const trimmed = message.trim();
  if (!trimmed || TECHNICAL_PHOTO_AI_TEXT.test(trimmed)) {
    return fallback;
  }
  return trimmed
    .replace(/^photo tools/i, "Background tools")
    .replace(/baseline photo tools/gi, "basic photo edits")
    .replace(/^loading .*? model$/i, "Setting up background tools")
    .replace(/^downloading .*$/i, "Setting up background tools");
}

function safePhotoAiError(message: string, fallback = "Photo tools couldn't finish. Try again.") {
  const trimmed = message.trim();
  if (!trimmed || TECHNICAL_PHOTO_AI_TEXT.test(trimmed)) {
    return fallback;
  }
  return trimmed
    .replace(/baseline photo tools/gi, "basic photo edits")
    .replace(/^photo processing failed/gi, "Photo tools couldn't finish");
}

function safePhotoAiCapabilities(capabilities: AiCapabilities): AiCapabilities {
  return {
    ...capabilities,
    message: safePhotoAiStatus(capabilities.message, capabilities.supported ? "Photo tools are ready." : "Private photo tools are unavailable on this device."),
    fallbackReason: capabilities.fallbackReason ? safePhotoAiError(capabilities.fallbackReason, "Private photo tools are unavailable on this device.") : undefined
  };
}

function createSharedWorker() {
  if (sharedWorker) {
    return sharedWorker;
  }

  const worker = new Worker(new URL("./photoAi.worker.ts", import.meta.url), { type: "module" });
  worker.onmessage = (event: MessageEvent<PhotoAiResponse>) => {
    const message = event.data;
    if (message.type === "progress") {
      const capabilities = message.capabilities ? safePhotoAiCapabilities(message.capabilities) : undefined;
      emitState((current) => ({
        ...current,
        capabilities: capabilities ?? (message.mode && current.capabilities ? { ...current.capabilities, mode: message.mode } : current.capabilities),
        isLoading: message.progress < 1 && current.isLoading,
        isProcessing: message.progress < 1 && current.isProcessing,
        progress: message.progress,
        status: safePhotoAiStatus(message.message)
      }));
      return;
    }

    const pending = sharedPending.get(message.id);
    if (!pending) {
      return;
    }
    sharedPending.delete(message.id);

    if (message.type === "error") {
      const safeMessage = safePhotoAiError(message.message);
      const error = new Error(safeMessage);
      emitState((current) => ({
        ...current,
        isLoading: false,
        isProcessing: false,
        error: safeMessage,
        status: safeMessage
      }));
      pending.reject(error);
      return;
    }

    if (message.type === "capabilities") {
      const capabilities = safePhotoAiCapabilities(message.capabilities);
      emitState((current) => ({
        ...current,
        capabilities,
        isLoading: false,
        isProcessing: false,
        error: capabilities.supported ? undefined : capabilities.message,
        progress: 1,
        status: capabilities.message
      }));
    }

    if (message.type === "result") {
      emitState((current) => ({
        ...current,
        isLoading: false,
        isProcessing: false,
        error: undefined,
        progress: 1,
        status: "Photo processing complete",
        lastResult: message.result,
        lastObjects: message.objects
      }));
    }

    if (message.type === "objects") {
      emitState((current) => ({
        ...current,
        isLoading: false,
        isProcessing: false,
        error: undefined,
        progress: 1,
        status: message.objects.length ? `${message.objects.length} foreground object${message.objects.length === 1 ? "" : "s"} detected` : "No foreground objects detected",
        lastObjects: message.objects
      }));
    }

    pending.resolve(message);
  };

  worker.onerror = (event) => {
    const safeMessage = safePhotoAiError(event.message || "Photo tools failed.");
    const error = new Error(safeMessage);
    for (const pending of sharedPending.values()) {
      pending.reject(error);
    }
    sharedPending.clear();
    sharedWorker = null;
    emitState((current) => ({
      ...current,
      isLoading: false,
      isProcessing: false,
      error: safeMessage,
      status: safeMessage
    }));
  };

  sharedWorker = worker;
  return worker;
}

function terminateSharedWorker(message = "Photo processing cancelled.") {
  if (sharedWorker) {
    sharedWorker.terminate();
    sharedWorker = null;
  }
  for (const pending of sharedPending.values()) {
    pending.reject(new Error(message));
  }
  sharedPending.clear();
}

export function usePhotoAi() {
  const [state, setState] = useState<PhotoAiState>(sharedState);

  const forceUnsupported = useMemo(() => {
    try {
      return localStorage.getItem("reos.photoAi.forceUnsupported") === "1";
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    subscribers.add(setState);
    setState(sharedState);
    return () => {
      subscribers.delete(setState);
    };
  }, []);

  const send = useCallback(
    async (request: RequestWithoutID) => {
      if (forceUnsupported && request.type !== "capabilities" && request.type !== "warmup") {
        throw new Error("Private photo tools are temporarily unavailable. Basic photo edits remain available.");
      }
      const worker = createSharedWorker();
      const id = `photo-ai-${Date.now()}-${sharedRequestCounter++}`;
      const fullRequest = { id, ...request } as PhotoAiRequest;
      emitState((current) => ({
        ...current,
        isLoading: request.type === "warmup" || request.type === "capabilities",
        isProcessing: request.type !== "warmup" && request.type !== "capabilities",
        error: undefined,
        progress: 0,
        status: request.type === "capabilities" ? "Checking photo tools" : request.type === "warmup" ? "Setting up photo tools" : "Starting photo processing"
      }));
      const promise = new Promise<PhotoAiResponse>((resolve, reject) => {
        sharedPending.set(id, { resolve, reject });
      });
      worker.postMessage(fullRequest);
      return promise;
    },
    [forceUnsupported]
  );

  const warmup = useCallback(async () => {
    const response = await send({ type: "warmup", forceUnsupported });
    if (response.type !== "capabilities") {
      throw new Error("Photo tools capability check failed.");
    }
    return response.capabilities;
  }, [forceUnsupported, send]);

  const getCapabilities = useCallback(async () => {
    const response = await send({ type: "capabilities", forceUnsupported });
    if (response.type !== "capabilities") {
      throw new Error("Photo tools capability check failed.");
    }
    return response.capabilities;
  }, [forceUnsupported, send]);

  const cancel = useCallback(() => {
    terminateSharedWorker("Photo processing cancelled.");
    emitState((current) => ({
      ...current,
      isLoading: false,
      isProcessing: false,
      error: undefined,
      status: "Photo processing cancelled",
      progress: 0
    }));
  }, []);

  const clearError = useCallback(() => {
    emitState((current) => ({
      ...current,
      error: undefined,
      status: current.isProcessing || current.isLoading ? current.status : "Photo tools idle",
      progress: current.isProcessing || current.isLoading ? current.progress : 0
    }));
  }, []);

  return {
    state,
    send,
    warmup,
    getCapabilities,
    cancel,
    clearError
  };
}
